import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { Shield, MapPin, AlertTriangle, Check } from "lucide-react";
import { api } from "@/lib/api";

function pin(color, label) {
  return L.divIcon({ className: "", html: `<div class="pin" style="width:24px;height:24px;background:${color};color:#fff;font-size:11px;">${label}</div>`, iconSize: [24, 24], iconAnchor: [12, 12] });
}
function Follow({ point }) {
  const map = useMap();
  useEffect(() => { if (point) map.panTo(point, { animate: true }); }, [point, map]);
  return null;
}

const STATUS = {
  active: { label: "On the way", color: "#16a34a" },
  arrived: { label: "Arrived safely", color: "#16a34a" },
  stalled: { label: "Not moving — check in on them", color: "#F59E0B" },
  overdue: { label: "Taking longer than expected", color: "#dc2626" },
};

export default function WalkWatch() {
  const { token } = useParams();
  const [w, setW] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/walks/token/${token}`).then((r) => { if (alive) setW(r.data); }).catch(() => { if (alive) setErr(true); });
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [token]);

  if (err) return <Center>This walk link is invalid or has ended.</Center>;
  if (!w) return <Center>Loading live location…</Center>;

  const st = STATUS[w.status] || STATUS.active;
  const walkerPos = [w.last_lat, w.last_lng];
  const destPos = [w.to_lat, w.to_lng];

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F5F4]" data-testid="walk-watch">
      <div className="p-4 bg-white border-b border-border">
        <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand flex items-center gap-1.5">
          <Shield size={12} strokeWidth={1.5} /> jeeo · Live walk
        </div>
        <h1 className="font-display text-xl font-semibold mt-0.5">{w.walker} is walking to {w.to_label}</h1>
        <div className="mt-2 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold"
          style={{ background: `${st.color}15`, color: st.color }} data-testid="watch-status">
          {w.status === "arrived" ? <Check size={16} /> : w.status === "stalled" || w.status === "overdue" ? <AlertTriangle size={16} /> : <MapPin size={16} />}
          {st.label}
          {w.status !== "arrived" && <span className="font-normal text-muted-foreground">· {w.eta_min} min · {w.remaining_m} m left</span>}
        </div>
      </div>
      <div className="flex-1">
        <MapContainer center={walkerPos} zoom={16} className="w-full h-full" zoomControl={false} data-testid="watch-map">
          <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
          <Follow point={walkerPos} />
          <Marker position={walkerPos} icon={pin("#7c3aed", "●")}><Popup>{w.walker}</Popup></Marker>
          <Marker position={destPos} icon={pin("#500000", "◎")}><Popup>{w.to_label}</Popup></Marker>
          <Polyline positions={[walkerPos, destPos]} pathOptions={{ color: "#7c3aed", weight: 4, opacity: 0.7, dashArray: "4 8" }} />
        </MapContainer>
      </div>
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#F5F5F4] p-6 text-center">
    <div className="mono text-sm tracking-[0.15em] uppercase text-brand">{children}</div>
  </div>
);
